// Exact-decimal money helpers for the admin previews.
//
// MONEY INVARIANT: we never represent a money amount as a binary float. These
// helpers parse a 2dp decimal STRING into integer CENTS, do the arithmetic on
// integers, and re-format back to a 2dp string. The only place a float briefly
// appears is the commission-rate multiply, which is immediately rounded to the
// nearest cent (half-up) before re-stringifying — so the preview a backend
// would recompute matches to the cent.

// Parse "1234.5" / "1234.56" / "1234" → integer cents. Returns null on garbage.
export function toCents(s: string): number | null {
  if (!/^\d+(\.\d{1,2})?$/.test(s.trim())) return null;
  const [whole, frac = ""] = s.trim().split(".");
  const cents = Number(whole) * 100 + Number((frac + "00").slice(0, 2));
  return Number.isFinite(cents) ? cents : null;
}

// Format integer cents → "X.XX".
export function fromCents(cents: number): string {
  const sign = cents < 0 ? "-" : "";
  const abs = Math.abs(Math.round(cents));
  const dollars = Math.floor(abs / 100);
  const rem = abs % 100;
  return `${sign}${dollars}.${String(rem).padStart(2, "0")}`;
}

// Normalize any accepted money string to canonical 2dp ("150" → "150.00").
export function fmtMoney(s: string): string {
  const c = toCents(s);
  return c === null ? s : fromCents(c);
}

// amount * rate (rate is a decimal string like "0.12"), rounded half-up to the
// nearest cent and returned as a 2dp string.
export function mulRate(amount: string, rate: string): string {
  const c = toCents(amount);
  if (c === null) return "0.00";
  const r = Number(rate);
  if (!Number.isFinite(r)) return "0.00";
  return fromCents(Math.round(c * r));
}

// a - b on two money strings → 2dp string (clamped at 0 is NOT applied; a real
// debit can exceed; callers pass sane inputs).
export function subCents(a: string, b: string): string {
  const ca = toCents(a);
  const cb = toCents(b);
  if (ca === null || cb === null) return "0.00";
  return fromCents(ca - cb);
}

// a + b on two money strings → 2dp string.
export function addCents(a: string, b: string): string {
  const ca = toCents(a);
  const cb = toCents(b);
  if (ca === null || cb === null) return "0.00";
  return fromCents(ca + cb);
}
